/**
 * Confirm Reset — interstitial that prevents email security scanners from consuming
 * the one-time password-recovery token. The email links here (not to Supabase verify)
 * with the token in the URL FRAGMENT (kept out of server/CDN logs), and we DO NOT
 * verify on page load. Only when the user clicks "Continue to reset password" do we
 * call verifyOtp({ token_hash, type:'recovery' }), which establishes the recovery
 * session and lets /reset-password set a new password.
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../components/common/Button";
import Card from "../components/ui/Card";
import { supabase } from "../services/supabase";
import {
  parseConfirmResetParams,
  parseConfirmResetHash,
} from "../utils/confirmResetUrl";

const RESET_VERIFICATION_TIMEOUT_MS = 15_000;

const ConfirmReset: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Token rides in the URL fragment (never sent to the server). Fall back to the
  // query string for any legacy in-flight links.
  const fromHash = parseConfirmResetHash(window.location.hash);
  const { tokenHash, type, valid } = fromHash.valid
    ? fromHash
    : parseConfirmResetParams(searchParams);

  const handleProceed = async () => {
    if (!tokenHash) {
      return;
    }
    setIsLoading(true);
    setError("");
    // Verify the recovery token ONLY now (on explicit click). This is the single-use
    // step a scanner's prefetch would otherwise have triggered.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const { error: verifyError } = await Promise.race([
        supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as "recovery",
        }),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("Reset verification timed out")),
            RESET_VERIFICATION_TIMEOUT_MS,
          );
        }),
      ]);

      if (verifyError) {
        setError(
          "This link could not be verified. It may have expired, already been used, or been opened by an email security scanner. Please request a new reset link and open only the newest email.",
        );
        return;
      }

      // Recovery session established — go set the new password.
      navigate("/reset-password");
    } catch {
      setError(
        "Reset-link verification took too long. Please check your connection and request a new reset link.",
      );
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/logowhite.png"
            alt="U.S. LawShield"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          This extra step protects your reset link — click to continue.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg">
          <div className="space-y-6">
            {valid ? (
              <>
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    For your security we don't open the reset link
                    automatically. Click below to continue and set a new
                    password.
                  </p>
                </div>

                {error && (
                  <div
                    role="alert"
                    className="p-4 rounded-lg bg-red-50 border border-red-200"
                  >
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <Button
                  variant="primary"
                  fullWidth
                  onClick={handleProceed}
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? "Verifying…" : "Continue to reset password"}
                </Button>

                {error && (
                  <div className="text-center">
                    <button
                      onClick={() => navigate("/forgot-password")}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Request a new reset link
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    This link is missing its reset token, so it can't be used.
                    Please request a new password reset link.
                  </p>
                </div>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => navigate("/forgot-password")}
                >
                  Request a new reset link
                </Button>
              </>
            )}

            <div className="text-center">
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel and go to login
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ConfirmReset;
